import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageChanger from "./LanguageChanger";
import "./Navigation.scss";

interface NavItem {
  labelKey: string;
  id?: string;
  href?: string;
}

const NAV_LINKS: NavItem[] = [
  { labelKey: "nav.product", id: "product" },
  { labelKey: "nav.tests", id: "tests" },
  { labelKey: "nav.howItWorks", id: "how-it-works" },
  { labelKey: "nav.results", id: "results" },
  { labelKey: "nav.studies", id: "studies" },
  { labelKey: "nav.blog", href: "/blog" },
  { labelKey: "nav.contact", id: "contact" },
];

interface NavigationProps {
  variant?: "landing" | "default";
}

export default function Navigation({ variant = "default" }: NavigationProps) {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = variant === "landing" || location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  function handleLinkClick(item: NavItem, e: React.MouseEvent) {
    if (item.href) return; // normal navigation
    e.preventDefault();

    if (isLanding) {
      // On landing page, smooth scroll
      document.getElementById(item.id!)?.scrollIntoView({ behavior: "smooth" });
    } else {
      // On other pages, navigate to landing with hash
      navigate(`/#${item.id}`);
    }
    setMobileOpen(false);
  }

  return (
    <nav className={`site-nav${scrolled ? " scrolled" : ""}${mobileOpen ? " mobile-open" : ""}`}>
      <div className="site-nav-inner">
        <a href="/" className="site-nav-logo">
          <img
            src="/img/logo.png"
            alt="Oculera"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </a>

        <button
          className="site-nav-hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={t("nav.toggleMenu")}
          aria-expanded={mobileOpen}
          aria-controls="site-nav-menu"
        >
          <span />
          <span />
          <span />
        </button>

        <div className="site-nav-menu" id="site-nav-menu">
          <ul className="site-nav-links">
            {NAV_LINKS.map((item) => (
              <li key={item.labelKey}>
                {item.href ? (
                  <a href={item.href} onClick={() => setMobileOpen(false)}>
                    {t(item.labelKey)}
                  </a>
                ) : (
                  <a href={`/#${item.id}`} onClick={(e) => handleLinkClick(item, e)}>
                    {t(item.labelKey)}
                  </a>
                )}
              </li>
            ))}
          </ul>

          <div className="site-nav-right">
            <LanguageChanger />
            <a href="/login" className="site-nav-login">
              {t("nav.login")}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
